import { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { BookOpen } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCabinModules } from '../hooks/cabin/useCabinModules';
import ModuleCard from '../components/cabin/ModuleCard';
import SkeletonModuleCard from '../components/cabin/SkeletonModuleCard';
import ModuleDetailSheet from '../components/cabin/ModuleDetailSheet';
import CabinErrorState from '../components/cabin/CabinErrorState';

const pageVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit:    { opacity: 0, y: -8 },
};

export default function CabinModulesList() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const shouldReduce = useReducedMotion();
  const { data: modules, isLoading, isError, refetch } = useCabinModules();
  const [selectedModule, setSelectedModule] = useState(null);

  const handleModuleNavigate = (mod) => {
    setSelectedModule(null);
    navigate(`/programs/kabin-memurlugu/modules/${mod.slug}`);
  };

  if (isError && !isLoading) {
    return (
      <CabinErrorState
        onRetry={refetch}
        backPath="/programs/kabin-memurlugu"
        testId="modules-list-error"
      />
    );
  }

  return (
    <motion.div
      variants={shouldReduce ? {} : pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.25, ease: 'easeOut' }}
      className="min-h-screen"
    >
      <div className="bg-gradient-to-r from-sky-600 via-blue-600 to-indigo-700 px-4 pt-6 pb-10 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <BookOpen size={28} className="text-white shrink-0" aria-hidden="true" />
          <div>
            <h1 className="text-2xl font-black text-white">{t('cabin.dashboard.modules_title')}</h1>
            <p className="text-sm text-sky-200">{t('cabin.dashboard.subtitle')}</p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 16 }).map((_, i) => <SkeletonModuleCard key={i} />)}
          </div>
        ) : !modules || modules.length === 0 ? (
          <div className="flex flex-col items-center gap-4 py-20 text-center" data-testid="modules-list-empty">
            <BookOpen size={48} className="text-[var(--text-muted)]" aria-hidden="true" />
            <p className="text-lg font-black text-[var(--text-main)]">
              {t('cabin.modules.empty.title') || 'Henüz modül eklenmedi'}
            </p>
            <p className="text-sm text-[var(--text-muted)]">
              {t('cabin.modules.empty.sub') || 'Modüller yakında yayına alınacak.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {(modules || []).map((mod) => (
              <ModuleCard
                key={mod.id || mod.module_id || mod.slug}
                module={mod}
                onClick={() => setSelectedModule(mod)}
              />
            ))}
          </div>
        )}
      </div>

      <ModuleDetailSheet
        module={selectedModule}
        open={!!selectedModule}
        onClose={() => setSelectedModule(null)}
        onNavigate={handleModuleNavigate}
      />
    </motion.div>
  );
}
